import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import type { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { assertNever } from 'src/replicant/utils';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { CollectionId } from 'src/replicant/cards/cards.ruleset';
import i18n from 'src/lib/i18n/i18n';
import { downscale } from 'src/conf/scale';
import { CardsProgressBar } from './CardsProgressBar';

type CommonState = {
  id: CollectionId;
  collected: number;
  totalCards: number;
};
type InProgress = {
  stateName: 'inProgress';
  rewards: Match2Reward[];
} & CommonState;
type RewardsAvailable = {
  stateName: 'rewardsAvailable';
  rewards: Match2Reward[];
} & CommonState;
type Complete = { stateName: 'complete' } & CommonState;
export type CardsHeaderState = InProgress | RewardsAvailable | Complete;

const REWARD_ICON = 'assets/ui/cards/common/icon_chest.png';

export class CardsHeader extends View {
  private title: LangBitmapFontTextShadowView;
  private progress: CardsProgressBar;

  constructor(opts: ViewOpts) {
    super({
      width: 640,
      height: 140,
      ...opts,
    });

    const titleBanner = new ImageScaleView({
      superview: this,
      image: 'assets/ui/cards/common/banner_title.png',
      width: downscale(576),
      height: downscale(132),
      x: this.style.width / 2,
      y: 0,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.title = new LangBitmapFontTextShadowView({
      superview: titleBanner,
      width: titleBanner.style.width - 90,
      height: 58,
      x: titleBanner.style.width / 2,
      y: titleBanner.style.height / 2 - 10,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 42,
      font: 'Title' as const,
      color: '#FFF5B5',
      textShadowColor: '#8D2E21',
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
    });

    this.progress = new CardsProgressBar({
      superview: this,
      x: this.style.width * 0.5,
      y: 90,
      width: this.style.width - 180,
      centerOnOrigin: true,
      leftIcon: 'assets/ui/cards/common/icon_cards.png',
      rightIcon: REWARD_ICON,
      rewardsTooltipPosition: 'below',
    });
  }

  public setState(state: CardsHeaderState) {
    this.setTitle(state.id);
    this.progress.setProgress(state.collected, state.totalCards);

    switch (state.stateName) {
      case 'inProgress':
      case 'rewardsAvailable':
        this.showProgress(state.rewards);
        break;
      case 'complete':
        this.showProgress();
        break;
      default:
        assertNever(state);
    }
  }

  private setTitle(collectionId: CollectionId) {
    this.title.localeText = () =>
      i18n(`cards.collections.${collectionId}.name`);
  }

  private showProgress(rewards?: Match2Reward[]) {
    this.progress.show();
    this.progress.setRightIcon(rewards ? REWARD_ICON : undefined);
    this.progress.setRewards(rewards);
  }
}
